import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { WidgetId } from '@/lib/telemetry';

/**
 * Identifies which widget (and at which layout position) a subtree belongs to,
 * so the shared WidgetCard shell can attribute impressions and action clicks
 * without every widget passing its own id around. `null` outside the grid —
 * consumers must treat telemetry as optional.
 */
export type WidgetIdentity = { id: WidgetId; position: number };

const WidgetIdentityContext = createContext<WidgetIdentity | null>(null);

export function WidgetIdentityProvider({
  id,
  position,
  children,
}: WidgetIdentity & { children: ReactNode }) {
  // Memoized so consumers' effects keyed on the identity don't re-fire (and
  // re-count impressions) every time the grid re-renders.
  const identity = useMemo(() => ({ id, position }), [id, position]);
  return <WidgetIdentityContext.Provider value={identity}>{children}</WidgetIdentityContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- tiny context pair; splitting hurts cohesion
export function useWidgetIdentity(): WidgetIdentity | null {
  return useContext(WidgetIdentityContext);
}
